import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader, AlertCircle, ChevronRight } from 'lucide-react';
import { QRCodeCanvas as QRCode } from 'qrcode.react';

const TransactionalDataPreview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionalData, setTransactionalData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // API Base URL
  const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

  // Fetch latest 5 transactional records
  const fetchTransactionalData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/getallproducts?limit=2&offset=0`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const data = await response.json();

      console.log('Transactional API Response:', data);

      // Handle API response format
      let dataList = [];
      let total = 0;

      if (data.success && data.data && data.data.records && Array.isArray(data.data.records)) {
        dataList = data.data.records;
        total = data.data.pagination?.total || dataList.length;
      } else if (data.data && data.data.records && Array.isArray(data.data.records)) {
        dataList = data.data.records;
        total = data.data.pagination?.total || dataList.length;
      } else if (data.records && Array.isArray(data.records)) {
        dataList = data.records;
        total = data.pagination?.total || dataList.length;
      } else if (Array.isArray(data)) {
        dataList = data;
        total = dataList.length;
      } else {
        console.error('Unexpected API response structure:', data);
        throw new Error('Invalid response format');
      }

      setTransactionalData(dataList);
      setTotalCount(total);

    } catch (err) {
      console.error('Error fetching transactional data:', err);
      setError(err.message || 'Failed to load transactional data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchTransactionalData();
  }, []);

  // Navigate to product passport
  const handleViewDigitalCopy = (item) => {
    navigate(`/product-passport/${item.id}`);
  };

  // Navigate to full transactional data page
  const handleViewTable = () => {
    navigate('/transaction-data');
  };

  // Loading State
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Transactional Data</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Transactional Data</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
          <p className="text-sm text-red-600 text-center">{error}</p>
          <button
            onClick={fetchTransactionalData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty State - No Data
  if (totalCount === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Transactional Data</h3>
          <button
            onClick={handleViewTable}
            className="px-4 py-2 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2 opacity-50 cursor-not-allowed"
            disabled
          >
            View Table
            {/* <ChevronRight className="w-4 h-4" /> */}
            <img src="/arrow.png" alt="Arrow Right" className="w-8 h-8" />
          </button>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-3">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-600 text-sm">No transactional data available</p>
        </div>
      </div>
    );
  }

  // Data Available - Show Table
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">
          Transactional Data
        </h3>
        <button
          onClick={handleViewTable}
          className="px-4 py-2 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          View Table
          {/* <ChevronRight className="w-4 h-4" /> */}
          <img src="/arrow.png" alt="Arrow Right" className="w-8 h-8" />
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead style={{ backgroundColor: '#D6E1F9' }}>
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">DPP ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Link to Digital Copy</th>
              {/* <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">DPP Created By</th> */}
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">QR Code</th>
            </tr>
          </thead>
          <tbody>
            {transactionalData.map((item, index) => (
              <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {item.id || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">
                  {item.product_id || 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleViewDigitalCopy(item)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Digital Copy
                  </button>
                </td>
                {/* <td className="px-4 py-3 text-sm text-gray-700">
                  {item.dpp_created_by || 'N/A'}
                </td> */}
                <td className="px-4 py-3 text-center">
                  {item.id ? (
                    <div className="inline-flex justify-center">
                      <QRCode
                        value={item.id}
                        size={60}
                        level="M"
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No QR</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet View - Cards */}
      <div className="lg:hidden p-4 space-y-3">
        {transactionalData.map((item, index) => (
          <div key={item.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">DPP ID</p>
                <p className="text-sm font-semibold text-gray-900 break-all">
                  {item.id || 'N/A'}
                </p>
              </div>
              <div className="ml-3">
                {item.id && (
                  <QRCode
                    value={item.id}
                    size={50}
                    level="M"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500">Product ID</p>
                <p className="text-sm font-mono text-gray-700">{item.product_id || 'N/A'}</p>
              </div>
              {/* <div>
                <p className="text-xs text-gray-500">DPP Created By</p>
                <p className="text-sm text-gray-700">{item.dpp_created_by || 'N/A'}</p>
              </div> */}
              <div className="pt-2">
                <button
                  onClick={() => handleViewDigitalCopy(item)}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Digital Copy
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionalDataPreview;